import { getSessionFn } from '@/server/session';
import { signOut } from '@frontend/auth/ssr/signOut';
import { useQuery } from '@tanstack/react-query';
import { Session } from '@frontend/auth/ssr/auth';
import { useServerFn } from '@tanstack/react-start';
import { useLocation, useNavigate } from '@tanstack/react-router';
import { createAuthorityFromUrl } from '@frontend/auth/ssr/utils';
import { useEffect } from 'react';
import { signIn } from '@frontend/auth/ssr/signIn';
import { appStore, useStore } from './useStore';

export const useSession = (): {
	isLoading: boolean;
	isAuthed: boolean;
	hydrated: boolean;
	session: Session;
	signOut?: () => void;
	signIn?: () => void;
	isDemo: boolean;
	isDemoSession: boolean;
} => {
	const fn = useServerFn(getSessionFn);
	const navigate = useNavigate();
	const location = useLocation();
	const isDemo = useStore(appStore, (s) => s.isDemo);
	const hydrated = useStore(appStore, (s) => s._hasHydrated);

	const { data, isLoading } = useQuery({
		queryKey: ['session'],
		queryFn: () => fn(),
		enabled: !!hydrated,
	});

	useEffect(() => {
		if (data?.session?.error) {
			navigate({ to: '/auth/logout' });
		}
	}, [data]);

	const DEMO_SESSION: Session = {
		expires: '',
		access_token: 'DEMO_TOKEN',
		user: {
			email: 'demo.demovich@demo.com',
			name: 'Demo',
			lastname: 'Demovich',
		},
		meta: {
			authority: 'demo',
			isDemo: true,
			tenantName: 'demo',
			isDevRoute: true,
		},
	};

	const session = data?.session ?? DEMO_SESSION;

	return {
		isLoading,
		isAuthed: session.access_token !== 'DEMO_TOKEN',
		hydrated: hydrated ?? false,
		session,
		signOut: data?.session
			? () =>
					signOut(
						createAuthorityFromUrl(
							location.url,
							import.meta.env.DEV
								? {
										isDevRoute: import.meta.env.VITE_TENANT_IS_DEV,
										tenantName: import.meta.env.VITE_TENANT_NAME,
									}
								: undefined,
						).url,
						session,
					)
			: undefined,
		signIn: !data?.session ? () => signIn() : undefined,
		isDemo: isDemo ?? false,
		isDemoSession: session.access_token === 'DEMO_TOKEN',
	};
};
